// Дрібні компоненти: ProgressBar, Kbd, Toolbar, Divider.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface ProgressBarProps {
  /** 0–100; undefined — невизначений (анімований) прогрес. */
  value?: number;
  tone?: 'primary' | 'accent' | 'success' | 'warning' | 'danger';
  /** Підпис над смугою (фаза генерації тощо). */
  label?: ReactNode;
  /** Показати відсоток праворуч від підпису. */
  showValue?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const fills = { primary: 'bg-primary-solid', accent: 'bg-accent', success: 'bg-success-fill', warning: 'bg-warning-fill', danger: 'bg-danger-fill' };

/** Смуга прогресу (генерація, імпорт). */
export function ProgressBar({ value, tone = 'primary', label, showValue = true, size = 'md', className }: ProgressBarProps) {
  const pct = value === undefined ? undefined : Math.max(0, Math.min(100, value));
  return (
    <div className={cn('w-full', className)}>
      {label || (showValue && pct !== undefined) ? (
        <div className="mb-1.5 flex items-center justify-between gap-3 text-[13px]">
          <span className="min-w-0 truncate text-fg-2">{label}</span>
          {showValue && pct !== undefined ? <span className="num shrink-0 font-semibold">{Math.round(pct)} %</span> : null}
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className={cn('relative w-full overflow-hidden rounded-full bg-surface-3', size === 'sm' ? 'h-1.5' : 'h-2')}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-300', fills[tone], pct === undefined && 'w-1/3 animate-pulse')}
          style={pct === undefined ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Клавіша (Ctrl, K, Esc). */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'num inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-border bg-surface-2 px-1 font-sans text-[11px] leading-none font-semibold text-muted',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export interface ToolbarProps {
  children: ReactNode;
  /** Праві елементи (дії). */
  end?: ReactNode;
  className?: string;
}

/** Рядок фільтрів/дій над таблицею чи календарем: зліва фільтри, справа дії; переноситься на вузьких екранах. */
export function Toolbar({ children, end, className }: ToolbarProps) {
  return (
    <div role="toolbar" className={cn('flex flex-wrap items-center gap-2', className)}>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</div>
      {end ? <div className="flex shrink-0 flex-wrap items-center gap-2">{end}</div> : null}
    </div>
  );
}

/** Горизонтальний розділювач. */
export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-0 border-t border-divider', className)} />;
}
